<script setup lang="ts">
  import { computed } from 'vue';
  import BillingPeriodToggle from './BillingPeriodToggle.vue';
  import { Globals } from '@/modules/Globals';
  import type {
    IBillingPlanCatalogItem,
    IBillingPlansResponse,
  } from '@/api/services/billing.service';

  const props = defineProps<{
    catalog: IBillingPlansResponse;
    selectedPeriod: number;
    mode: 'purchase' | 'change';
    busy?: boolean;
    currentTier?: number | null;
    currentPeriodMonths?: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'period-change', value: number): void;
    (e: 'select-plan', plan: IBillingPlanCatalogItem): void;
  }>();

  const planMeta = {
    0: {
      highlight: false,
      icon: 'mdi-leaf',
      description: 'Para empezar y probar el panel.',
      features: [
        'Menú QR y catálogo',
        'Categorías y productos',
        'Pedidos básicos',
      ],
      cta: 'Incluido',
    },
    1: {
      highlight: false,
      icon: 'mdi-rocket',
      description: 'Ideal para la mayoría de los locales.',
      features: [
        'Menú QR y catálogo',
        'Categorías y productos ilimitados',
        'Pedidos en tiempo real',
        'Pedidos en mesa, take away y delivery',
        'Pedidos por WhatsApp o QueResto',
        'Punto de venta (PoS)',
        'Administración de inventario',
        'Administración de usuarios',
        'Estadísticas detalladas',
        'Módulo de Caja',
        'Personaliza el diseño',
        'Soporte estándar',
      ],
      cta: 'Elegir Plus',
    },
    2: {
      highlight: true,
      icon: 'mdi-crown',
      description: 'Para locales que quieren todo.',
      features: [
        'Menú QR y catálogo',
        'Categorías y productos ilimitados',
        'Pedidos en tiempo real',
        'Pedidos en mesa, take away y delivery',
        'Pedidos por WhatsApp o QueResto',
        'Punto de venta (PoS)',
        'Administración de inventario',
        'Administración de usuarios',
        'Estadísticas detalladas',
        'Módulo de Caja',
        'Personaliza el diseño',
        'Menús diferentes según tipo de pedido',
        'Sucursales ilimitadas',
        'Mesas avanzadas (hasta 500)',
        'Módulo de Etiquetas',
        'Facturación electrónica (AR)',
        'Soporte prioritario',
      ],
      cta: 'Elegir Pro',
    },
  } as const;

  const periods = computed(() => {
    if (props.mode === 'change') {
      const locked = props.currentPeriodMonths ?? null;
      if (locked === 1 || locked === 12) return [locked];
    }
    const raw = props.catalog?.periods ?? [1, 12];
    return raw.filter((p) => p === 1 || p === 12);
  });

  const periodTitle = computed(() => {
    return props.mode === 'change'
      ? 'Duración (bloqueada)'
      : 'Elija la duración';
  });

  const periodNote = computed(() => {
    if (props.mode === 'change') {
      return 'La duración se mantiene al cambiar de plan.';
    }
    return 'Pagos seguros. Renovación automática.';
  });

  const annualDiscountPercent = computed(() => {
    if (!periods.value.includes(12)) return null;
    const plans = (props.catalog?.plans ?? [])
      .filter((p) => p.tier > 0)
      .slice()
      .sort((a, b) => b.tier - a.tier);
    const refPlan = plans[0] ?? null;
    if (!refPlan) return null;

    const monthly = refPlan.variants?.[1];
    const annual = refPlan.variants?.[12];
    if (typeof monthly !== 'number' || typeof annual !== 'number') return null;
    if (monthly <= 0) return null;

    const pct = (1 - annual / (monthly * 12)) * 100;
    if (!Number.isFinite(pct) || pct <= 0) return null;
    return pct;
  });

  const selectedCurrency = computed(() => {
    const currencyId = props.catalog?.currency ?? 'USD';
    return Globals.getCurrency(currencyId);
  });

  const displayPlans = computed(() => {
    const plans = (props.catalog?.plans ?? []).filter((p) => p.tier > 0);
    return plans
      .slice()
      .sort((a, b) => a.tier - b.tier)
      .map((p) => ({
        ...p,
        meta: planMeta[p.tier as 0 | 1 | 2] ?? planMeta[0],
      }));
  });

  function formatMoney(amount: number) {
    const cur = selectedCurrency.value;
    const symbol = cur?.symbol ?? '$';
    const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
    const useDecimals = rounded % 1 !== 0;
    const formatted = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: useDecimals ? 2 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0,
    }).format(rounded);
    return `${symbol}${formatted}`;
  }

  function getVariant(plan: IBillingPlanCatalogItem, months: number) {
    return plan.variants?.[months] ?? null;
  }

  function monthlyEquivalent(plan: IBillingPlanCatalogItem, months: number) {
    const total = getVariant(plan, months);
    if (total == null) return null;
    return Math.round((total / months) * 100) / 100;
  }

  function savings(plan: IBillingPlanCatalogItem, months: number) {
    const monthly = getVariant(plan, 1);
    const total = getVariant(plan, months);
    if (monthly == null || total == null) return null;
    const save = monthly * months - total;
    return save > 0 ? Math.round(save * 100) / 100 : null;
  }

  function savingsPercent(plan: IBillingPlanCatalogItem, months: number) {
    if (months === 1) return null;
    const monthly = getVariant(plan, 1);
    const total = getVariant(plan, months);
    if (monthly == null || total == null) return null;
    if (monthly <= 0) return null;
    const pct = (1 - total / (monthly * months)) * 100;
    if (!Number.isFinite(pct) || pct <= 0) return null;
    return Math.round(pct);
  }

  function originalTotal(plan: IBillingPlanCatalogItem, months: number) {
    if (months === 1) return null;
    const monthly = getVariant(plan, 1);
    if (monthly == null) return null;
    return monthly * months;
  }

  function monthlyEquivalentText(
    plan: IBillingPlanCatalogItem,
    months: number
  ) {
    const v = monthlyEquivalent(plan, months);
    return v == null ? '-' : formatMoney(v);
  }

  function totalText(plan: IBillingPlanCatalogItem, months: number) {
    const v = getVariant(plan, months);
    return v == null ? null : formatMoney(v);
  }

  function savingsText(plan: IBillingPlanCatalogItem, months: number) {
    const v = savings(plan, months);
    return v == null ? null : formatMoney(v);
  }

  function originalTotalText(plan: IBillingPlanCatalogItem, months: number) {
    const v = originalTotal(plan, months);
    return v == null ? null : formatMoney(v);
  }

  function isCurrent(plan: IBillingPlanCatalogItem) {
    if (props.mode !== 'change') return false;
    if (props.currentTier == null || props.currentPeriodMonths == null)
      return false;
    return (
      plan.tier === props.currentTier &&
      props.selectedPeriod === props.currentPeriodMonths
    );
  }

  function ctaText(plan: IBillingPlanCatalogItem, title: string) {
    if (plan.tier === 0) return planMeta[0].cta;
    if (isCurrent(plan)) return 'Plan actual';
    if (props.mode === 'change') return `Cambiar a ${title}`;
    return (planMeta[plan.tier as 1 | 2] ?? planMeta[0]).cta;
  }
</script>

<template>
  <div class="billing-plan-picker">
    <div class="billing-plan-picker__period">
      <div class="billing-plan-picker__period-title">{{ periodTitle }}</div>
      <BillingPeriodToggle
        :value="selectedPeriod"
        :periods="periods"
        :annual-discount-percent="annualDiscountPercent"
        @input="emit('period-change', $event)"
      />
      <div class="billing-plan-picker__period-note">
        <v-icon class="billing-plan-picker__period-note-icon" small
          >mdi-lock</v-icon
        >
        <span>{{ periodNote }}</span>
      </div>
    </div>

    <v-row class="billing-plan-picker__grid" align="stretch" justify="center">
      <v-col
        v-for="p in displayPlans"
        :key="`${p.tier}-${p.currency}`"
        cols="12"
        sm="10"
        md="6"
        lg="4"
      >
        <v-card
          class="billing-plan-card"
          :class="{
            'billing-plan-card--featured': p.meta.highlight,
            'billing-plan-card--free': p.tier === 0,
            'billing-plan-card--current': isCurrent(p),
          }"
          :elevation="p.meta.highlight ? 16 : 6"
        >
          <div class="billing-plan-card__header">
            <div class="billing-plan-card__header-top">
              <div class="billing-plan-card__icon">
                <v-icon :class="['billing-plan-card__icon-i']" size="22">
                  {{ p.meta.icon }}
                </v-icon>
              </div>

              <div class="billing-plan-card__badges">
                <div
                  v-if="p.meta.highlight"
                  class="billing-plan-card__badge billing-plan-card__badge--featured"
                >
                  Recomendado
                </div>
                <div
                  v-else-if="isCurrent(p)"
                  class="billing-plan-card__badge billing-plan-card__badge--current"
                >
                  Plan actual
                </div>
              </div>
            </div>

            <div class="billing-plan-card__title">{{ p.title }}</div>
            <div class="billing-plan-card__description">
              {{ p.meta.description }}
            </div>

            <div
              v-if="selectedPeriod !== 1 && savingsPercent(p, selectedPeriod)"
              class="billing-plan-card__discount-pill"
            >
              <v-icon class="billing-plan-card__discount-pill-icon" size="16">
                mdi-tag
              </v-icon>
              <span> Ahorrá {{ savingsPercent(p, selectedPeriod) }}% </span>
            </div>
          </div>

          <v-card-text class="billing-plan-card__body">
            <div class="billing-plan-card__price">
              <div class="billing-plan-card__price-row">
                <span class="billing-plan-card__price-amount">
                  {{
                    p.tier === 0
                      ? 'Gratis'
                      : monthlyEquivalentText(p, selectedPeriod)
                  }}
                </span>
                <span v-if="p.tier !== 0" class="billing-plan-card__price-unit"
                  >/mes</span
                >
              </div>

              <div class="billing-plan-card__billing">
                <div class="billing-plan-card__billing-row">
                  <span class="billing-plan-card__billing-label">Total</span>
                  <span class="billing-plan-card__billing-value">
                    <span v-if="p.tier === 0">—</span>
                    <span v-else-if="totalText(p, selectedPeriod) !== null">
                      {{ totalText(p, selectedPeriod) }}
                      <span
                        v-if="selectedPeriod !== 1"
                        class="billing-plan-card__billing-suffix"
                      >
                        /año
                      </span>
                    </span>
                    <span v-else>—</span>
                  </span>
                </div>

                <div
                  v-if="
                    p.tier !== 0 &&
                    selectedPeriod !== 1 &&
                    originalTotalText(p, selectedPeriod)
                  "
                  class="billing-plan-card__billing-row billing-plan-card__billing-row--muted"
                >
                  <span class="billing-plan-card__billing-label">Antes</span>
                  <span
                    class="billing-plan-card__billing-value billing-plan-card__billing-value--striked"
                  >
                    {{ originalTotalText(p, selectedPeriod) }}
                  </span>
                </div>

                <div
                  v-if="
                    p.tier !== 0 &&
                    selectedPeriod !== 1 &&
                    savingsText(p, selectedPeriod) !== null
                  "
                  class="billing-plan-card__savings"
                >
                  <v-icon class="billing-plan-card__savings-icon" size="18">
                    mdi-piggy-bank-outline
                  </v-icon>
                  <span>
                    Ahorra {{ savingsText(p, selectedPeriod) }}
                    <span
                      v-if="savingsPercent(p, selectedPeriod)"
                      class="billing-plan-card__savings-pct"
                    >
                      ({{ savingsPercent(p, selectedPeriod) }}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div class="billing-plan-card__divider" />

            <div class="billing-plan-card__features">
              <div class="billing-plan-card__features-title">Incluye</div>
              <ul class="billing-plan-card__features-list">
                <li
                  v-for="(f, idx) in p.meta.features"
                  :key="idx"
                  class="billing-plan-card__feature"
                >
                  <v-icon class="billing-plan-card__feature-icon" size="18">
                    mdi-check-circle
                  </v-icon>
                  <span class="billing-plan-card__feature-text">{{ f }}</span>
                </li>
              </ul>
            </div>
          </v-card-text>

          <v-card-actions class="billing-plan-card__actions">
            <v-btn
              v-if="p.tier === 0"
              class="billing-plan-card__cta"
              block
              depressed
              disabled
            >
              {{ ctaText(p, p.title) }}
            </v-btn>

            <v-btn
              v-else
              class="billing-plan-card__cta"
              :class="{ 'billing-plan-card__cta--featured': p.meta.highlight }"
              block
              large
              :disabled="busy || isCurrent(p)"
              @click="emit('select-plan', p)"
            >
              <v-icon left>mdi-arrow-right</v-icon>
              {{ ctaText(p, p.title) }}
            </v-btn>

            <div class="billing-plan-card__cta-note">
              <v-icon class="billing-plan-card__cta-note-icon" size="16">
                mdi-shield-check
              </v-icon>
              <span>Checkout seguro. Cancele cuando quiera.</span>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
  .billing-plan-picker {
    &__period {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      text-align: center;
      margin-bottom: 18px;
    }

    &__period-title {
      font-size: 14px;
      font-weight: 800;
      color: rgba(15, 23, 42, 0.72);
      letter-spacing: 0.2px;
    }

    &__period-note {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.56);
    }

    &__period-note-icon {
      color: rgba(15, 23, 42, 0.48);
    }

    &__grid {
      margin-top: 2px;
    }
  }

  .billing-plan-card {
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.08);
    transition: transform 0.18s ease, box-shadow 0.18s ease,
      border-color 0.18s ease;

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(255, 111, 0, 0.22);
    }

    &--featured {
      border-color: rgba(255, 111, 0, 0.35);
      box-shadow: 0 22px 46px rgba(255, 111, 0, 0.14);
    }

    &--free {
      opacity: 0.86;

      &:hover {
        transform: none;
        border-color: rgba(15, 23, 42, 0.08);
      }
    }

    &__header {
      padding: 18px 18px 16px;
      background: linear-gradient(
        180deg,
        rgba(255, 111, 0, 0.08),
        rgba(255, 255, 255, 0)
      );
    }

    &--featured &__header {
      background: linear-gradient(
        135deg,
        rgba(255, 111, 0, 0.22),
        rgba(255, 152, 0, 0.08)
      );
    }

    &__header-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 10px;
    }

    &__icon {
      width: 38px;
      height: 38px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(15, 23, 42, 0.06);
    }

    &--featured &__icon {
      background: rgba(255, 111, 0, 0.12);
    }

    &__icon-i {
      color: rgba(15, 23, 42, 0.78);
    }

    &--featured &__icon-i {
      color: rgba(120, 53, 15, 0.98);
    }

    &__badges {
      display: inline-flex;
      gap: 8px;
      align-items: center;
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 10px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.2px;

      &--featured {
        background: rgba(255, 111, 0, 0.92);
        color: #fff;
      }

      &--current {
        background: rgba(34, 197, 94, 0.12);
        color: rgba(20, 83, 45, 0.98);
      }
    }

    &__title {
      font-size: 20px;
      font-weight: 900;
      letter-spacing: -0.2px;
      color: rgba(15, 23, 42, 0.92);
      margin-bottom: 2px;
    }

    &__description {
      font-size: 13px;
      color: rgba(15, 23, 42, 0.62);
    }

    &__discount-pill {
      margin-top: 12px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 999px;
      background: rgba(255, 111, 0, 0.12);
      color: rgba(120, 53, 15, 0.98);
      font-weight: 900;
      font-size: 12px;
      width: fit-content;
    }

    &__discount-pill-icon {
      color: rgba(120, 53, 15, 0.9);
    }

    &__body {
      padding: 16px 18px 10px !important;
    }

    &__price-row {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 10px;
    }

    &__price-amount {
      font-size: 34px;
      font-weight: 900;
      letter-spacing: -0.6px;
      color: rgba(15, 23, 42, 0.96);
      line-height: 1.1;
    }

    &__price-unit {
      font-size: 13px;
      font-weight: 800;
      color: rgba(15, 23, 42, 0.58);
    }

    &__billing {
      display: grid;
      gap: 6px;
    }

    &__billing-row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 10px;
      font-size: 13px;

      &--muted {
        color: rgba(15, 23, 42, 0.55);
      }
    }

    &__billing-label {
      color: rgba(15, 23, 42, 0.62);
      font-weight: 700;
    }

    &__billing-value {
      font-weight: 900;
      color: rgba(15, 23, 42, 0.86);
      text-align: right;

      &--striked {
        text-decoration: line-through;
        text-decoration-thickness: 2px;
        text-decoration-color: rgba(239, 68, 68, 0.55);
        color: rgba(15, 23, 42, 0.55);
        font-weight: 800;
      }
    }

    &__billing-suffix {
      font-weight: 800;
      color: rgba(15, 23, 42, 0.52);
      margin-left: 2px;
    }

    &__savings {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: 12px;
      background: rgba(34, 197, 94, 0.1);
      color: rgba(20, 83, 45, 0.98);
      font-weight: 900;
      font-size: 13px;
      margin-top: 4px;
      width: fit-content;
    }

    &__savings-icon {
      color: rgba(20, 83, 45, 0.9);
    }

    &__savings-pct {
      font-weight: 900;
      opacity: 0.9;
    }

    &__divider {
      height: 1px;
      background: rgba(15, 23, 42, 0.08);
      margin: 14px 0 12px;
    }

    &__features-title {
      font-size: 13px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.72);
      margin-bottom: 10px;
    }

    &__features-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: grid;
      gap: 8px;
    }

    &__feature {
      display: grid;
      grid-template-columns: 18px 1fr;
      gap: 10px;
      align-items: start;
      font-size: 13px;
      color: rgba(15, 23, 42, 0.74);
    }

    &__feature-icon {
      color: rgba(34, 197, 94, 0.98);
      margin-top: 1px;
    }

    &__actions {
      padding: 16px 18px 18px !important;
      display: grid;
      gap: 10px;
      background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0),
        rgba(15, 23, 42, 0.02)
      );
    }

    &__cta {
      border-radius: 14px;
      height: 50px;
      font-weight: 900;
      letter-spacing: 0.2px;
      text-transform: none;
      background: rgba(15, 23, 42, 0.92) !important;
      color: #fff !important;
      box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18) !important;

      &--featured {
        background: linear-gradient(135deg, #ff6f00, #ff9800) !important;
        box-shadow: 0 18px 34px rgba(255, 111, 0, 0.24) !important;
      }

      &[disabled] {
        box-shadow: none !important;
        background: rgba(15, 23, 42, 0.12) !important;
        color: rgba(15, 23, 42, 0.46) !important;
      }
    }

    &__cta-note {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.56);
    }

    &__cta-note-icon {
      color: rgba(34, 197, 94, 0.9);
    }
  }
</style>
